import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const fmt = (n) => "€" + Number(n).toFixed(2).replace(".", ",");
const MAX_ATTEMPTS = 7;
const INTERVAL = 2000;

export default function OrderConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("polling"); // polling | success | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("success");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, INTERVAL);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="empty-state" data-testid="order-confirmed">
      {state === "polling" && (
        <>
          <Loader2 size={42} color="var(--gold)" className="spin" style={{ margin: "0 auto 1.5rem", animation: "spin 1s linear infinite" }} />
          <h2>Vérification de votre paiement…</h2>
          <p className="section-body">Merci de patienter quelques instants.</p>
        </>
      )}

      {state === "success" && (
        <>
          <CheckCircle2 size={52} color="#0a4a38" style={{ margin: "0 auto 1.5rem" }} />
          <h2>Merci pour votre commande !</h2>
          <p className="section-body" data-testid="order-success-msg">
            Votre paiement a bien été confirmé. Un email de confirmation vous a été envoyé.
          </p>
          {order ? (
            <div style={{ maxWidth: 420, margin: "0 auto 2rem", textAlign: "left", background: "var(--white)", border: "1px solid rgba(201,169,110,0.25)", padding: "1.5rem" }}>
              {order.items.map((it) => (
                <div className="summary-row" key={it.product_id}>
                  <span>{it.name} × {it.quantity}</span>
                  <span>{fmt(it.line_total)}</span>
                </div>
              ))}
              <div className="summary-row total"><span>Total payé</span><span>{fmt(order.amount)}</span></div>
            </div>
          ) : null}
          <Link to="/collections/all" className="btn-primary" data-testid="order-continue">Continuer mes achats</Link>
        </>
      )}

      {state === "pending" && (
        <>
          <Loader2 size={42} color="var(--gold)" style={{ margin: "0 auto 1.5rem" }} />
          <h2>Paiement en cours de traitement</h2>
          <p className="section-body">Votre paiement est en cours de validation. Vous recevrez un email dès confirmation.</p>
          <Link to="/" className="btn-primary">Retour à l'accueil</Link>
        </>
      )}

      {state === "error" && (
        <>
          <XCircle size={52} color="#8a2a2a" style={{ margin: "0 auto 1.5rem" }} />
          <h2>Paiement non confirmé</h2>
          <p className="section-body">Votre paiement n'a pas pu être confirmé ou a été annulé.</p>
          <Link to="/panier" className="btn-primary" data-testid="order-retry">Retour au panier</Link>
        </>
      )}
    </div>
  );
}
